import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  KeyboardEvent,
  ReactNode,
} from 'react';
import { SendHorizontal, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';

const textareaClassName =
  'min-h-9 max-h-[150px] resize-none overflow-y-auto border-0 bg-transparent dark:bg-transparent px-3 py-1.5 text-sm leading-normal shadow-none outline-none ring-0 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border';

export interface ChatInputProps {
  /** Callback when a message is submitted */
  onSubmit: (message: string) => void;
  onInputChange?: (value: string) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether a message is being sent */
  isLoading?: boolean;
  /** Auto-focus the input */
  autoFocus?: boolean;
  /** Focus the input when this becomes true (useful for focusing on events) */
  focusTrigger?: boolean;
  /** Content rendered on the left side of the footer bar (e.g. model selector) */
  footerLeft?: ReactNode;
  accentTone?: 'default' | 'amber';
  maxLength?: number;
  /**
   * When provided, a submit made while `isLoading` queues the message
   * instead of being refused: typing stays enabled through the turn and
   * the send action hands the text here (the prompt-queue strip shows it
   * until dispatch). Without it, `isLoading` disables the input as before.
   */
  onQueueMessage?: (message: string) => void;
  /**
   * Registers a setter an external control (the queue strip's
   * click-to-edit) uses to pop text into the composer. May return a
   * cleanup that unregisters it.
   */
  registerDraftSetter?: (setter: (text: string) => void) => (() => void) | void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSubmit,
  onInputChange,
  placeholder = 'Type something ...',
  disabled = false,
  isLoading = false,
  autoFocus = false,
  focusTrigger = false,
  footerLeft,
  accentTone = 'default',
  maxLength,
  onQueueMessage,
  registerDraftSetter,
}) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Ref so the draft setter registered below never closes over a stale
  // onInputChange.
  const onInputChangeRef = useRef(onInputChange);
  onInputChangeRef.current = onInputChange;

  useEffect(() => {
    if (!registerDraftSetter) return;
    return registerDraftSetter((text: string) => {
      setValue(text);
      onInputChangeRef.current?.(text);
      textareaRef.current?.focus();
    });
  }, [registerDraftSetter]);

  const handleSubmit = () => {
    const trimmedValue = value.trim();
    if (!trimmedValue || disabled) return;
    if (isLoading) {
      // Mid-turn the send action queues instead of sending: the prompt
      // joins the strip above the composer and dispatches once the agent
      // is idle again.
      if (!onQueueMessage) return;
      onQueueMessage(trimmedValue);
    } else {
      onSubmit(trimmedValue);
    }
    setValue('');
    onInputChange?.('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const next = Math.min(textarea.scrollHeight, 150);
    textarea.style.height = `${next}px`;
  }, [value]);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  // Typing stays available mid-turn when queueing is wired: the send
  // action then queues the prompt instead of being refused.
  const isDisabled = disabled || (isLoading && !onQueueMessage);

  useEffect(() => {
    if (focusTrigger && !isDisabled && textareaRef.current) {
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  }, [focusTrigger, isDisabled]);

  const canSubmit = value.trim().length > 0 && !isDisabled;

  const actionControl =
    isLoading && !onQueueMessage ? (
      <div
        className='flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground'
        role='status'
        aria-label='Agent is responding'
      >
        <Loader2 className='size-5 animate-spin' aria-hidden />
      </div>
    ) : (
      <button
        type='button'
        onClick={handleSubmit}
        aria-label={isLoading ? 'Queue message' : 'Send message'}
        disabled={!canSubmit}
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-full transition-colors',
          canSubmit
            ? accentTone === 'amber'
              ? 'bg-amber-500 text-white hover:bg-amber-600 cursor-pointer'
              : 'bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer'
            : 'bg-muted text-muted-foreground/40 cursor-not-allowed',
        )}
      >
        <SendHorizontal className='size-4' />
      </button>
    );

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-col gap-1 rounded-3xl border border-border bg-sidebar p-1'>
        <div className='flex min-h-0 min-w-0 items-start gap-1 px-0.5 pt-0.5'>
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              const next = e.target.value;
              setValue(next);
              onInputChange?.(next);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            aria-label={placeholder || 'Chat message input'}
            disabled={isDisabled}
            maxLength={maxLength}
            rows={1}
            className={cn(textareaClassName, 'w-full min-w-0 flex-1')}
          />
        </div>

        <div className='flex items-center justify-between pl-2 pr-1 pb-1 pt-0'>
          <div className='flex items-center gap-1'>{footerLeft}</div>
          {actionControl}
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
